/**
 * Hook for Payment Mandates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mandateApi, goCardlessApi } from '../services/mandate.service';
import type {
  PaymentMandate,
  InitiateGoCardlessMandateRequest,
  InitiateStripeMandateRequest,
  CreateManualMandateRequest,
  MandateStatus,
  MandateProvider,
} from '../types/mandate.types';

const MANDATES_KEY = ['payment-mandates'];
const GOCARDLESS_CONFIG_KEY = ['gocardless-config'];

/**
 * Hook to list all mandates
 */
export function useMandates(options?: {
  status?: MandateStatus;
  provider?: MandateProvider;
}) {
  return useQuery({
    queryKey: [...MANDATES_KEY, options],
    queryFn: () => mandateApi.list(options),
  });
}

/**
 * Hook to get mandate by ID
 */
export function useMandate(id: string | undefined) {
  return useQuery({
    queryKey: [...MANDATES_KEY, id],
    queryFn: () => mandateApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to get mandates for a client
 */
export function useClientMandates(clientId: string | undefined) {
  return useQuery({
    queryKey: [...MANDATES_KEY, 'client', clientId],
    queryFn: () => mandateApi.getByClient(clientId!),
    enabled: !!clientId,
  });
}

/**
 * Hook to get active mandate for a client
 */
export function useActiveClientMandate(clientId: string | undefined) {
  return useQuery({
    queryKey: [...MANDATES_KEY, 'client', clientId, 'active'],
    queryFn: () => mandateApi.getActiveByClient(clientId!),
    enabled: !!clientId,
  });
}

/**
 * Hook to initiate GoCardless mandate
 */
export function useInitiateGoCardlessMandate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InitiateGoCardlessMandateRequest) =>
      mandateApi.initiateGoCardless(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANDATES_KEY });
    },
  });
}

/**
 * Hook to complete GoCardless mandate
 */
export function useCompleteGoCardlessMandate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (redirectFlowId: string) =>
      mandateApi.completeGoCardless({ redirectFlowId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANDATES_KEY });
    },
  });
}

/**
 * Hook to initiate Stripe mandate
 */
export function useInitiateStripeMandate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InitiateStripeMandateRequest) =>
      mandateApi.initiateStripe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANDATES_KEY });
    },
  });
}

/**
 * Hook to complete Stripe mandate
 */
export function useCompleteStripeMandate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (setupIntentId: string) =>
      mandateApi.completeStripe({ setupIntentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANDATES_KEY });
    },
  });
}

/**
 * Hook to create manual mandate
 */
export function useCreateManualMandate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateManualMandateRequest) => mandateApi.createManual(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANDATES_KEY });
    },
  });
}

/**
 * Hook to cancel mandate
 */
export function useCancelMandate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      mandateApi.cancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MANDATES_KEY });
    },
  });
}

/**
 * Hook to get GoCardless configuration
 */
export function useGoCardlessConfig() {
  return useQuery({
    queryKey: GOCARDLESS_CONFIG_KEY,
    queryFn: async () => {
      const config = await goCardlessApi.getConfig();
      // React Query v5 doesn't allow undefined, ensure we return null
      return config ?? null;
    },
  });
}

/**
 * Hook to configure GoCardless
 */
export function useConfigureGoCardless() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: goCardlessApi.configure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOCARDLESS_CONFIG_KEY });
    },
  });
}

/**
 * Hook to disable GoCardless
 */
export function useDisableGoCardless() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: goCardlessApi.disable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GOCARDLESS_CONFIG_KEY });
    },
  });
}
